/**
 * GRN Allocation Repair — superuser tool API.
 *
 * Backed by ``purchasing/views/grn_allocation_admin.py``: lists PO lines whose
 * GRNs the automatic attribution left on the synthetic line-0 anchor (or any
 * other misaligned slot), and lets a superuser drag GRNs onto the right
 * schedule. The link-schedule endpoint is the existing ``GoodsReceiptNoteViewSet``
 * action — same path the import pipeline uses for re-allocation.
 */
import api, { type AxiosResponse } from './client';

export interface RepairPoLineRow {
  po_line_id: number;
  po_number: string;
  line_number: string;
  part_number: string;
  supplier_id: number | null;
  supplier_name: string | null;
  bad_grn_count: number;
  last_receipt: string | null;
}

export interface RepairPoLineListResponse {
  count: number;
  page: number;
  page_size: number;
  /** Schedule-level total for the date buckets (arrears/due-this/due-next),
   *  so it reconciles with the Schedules tabs. null for line0/all. */
  schedule_count: number | null;
  results: RepairPoLineRow[];
}

export interface RepairSchedule {
  id: number;
  schedule_line_number: string;
  schedule_date: string | null;
  schedule_qty: string;
  shipped_qty: string;
  balance_qty: string;
  allocated_qty: string;
  status: string;
  is_synthetic: boolean;
}

export interface RepairGRN {
  id: number;
  grn_number: string;
  receipt_date: string;
  quantity_received: string;
  po_line_schedule_id: number | null;
  on_time_status: string | null;
}

export interface RepairAllocation {
  id: number;
  grn_id: number;
  po_line_schedule_id: number;
  allocated_qty: string;
  on_time_status: string | null;
  is_over_delivery: boolean;
}

export interface RepairPoLineDetail {
  po_line: {
    id: number;
    po_number: string;
    line_number: string;
    part_number: string;
    supplier_id: number | null;
    supplier_name: string | null;
    customer_id: number;
    erp_order_type: string;
  };
  otif_window: { days_early: number; days_late: number };
  schedules: RepairSchedule[];
  grns: RepairGRN[];
  allocations: RepairAllocation[];
}

export type RepairFilterMode =
  | 'line0'
  | 'arrears'
  | 'due_this_week'
  | 'due_next_week'
  | 'all'
  // Back-compat for older clients — server maps it to 'arrears'.
  | 'due_last_week_pending';

export interface RepairListParams {
  customer_id?: number;
  supplier_id?: number;
  supplier_search?: string;
  since?: string;
  po_search?: string;
  part_search?: string;
  filter_mode?: RepairFilterMode;
  /** This week's Monday (yyyy-mm-dd), browser-derived, so the date buckets
   *  use the same week boundary as the Schedules tabs. */
  week_start?: string;
  page?: number;
  page_size?: number;
}

export const grnRepairAPI = {
  listPoLines: (params?: RepairListParams): Promise<AxiosResponse<RepairPoLineListResponse>> =>
    api.get('/purchasing/grns/repair-po-lines/', { params }),

  getPoLineDetail: (po_line_id: number): Promise<AxiosResponse<RepairPoLineDetail>> =>
    api.get('/purchasing/grns/repair-po-line-detail/', { params: { po_line_id } }),

  // Reuses the existing link-schedule superuser action — atomic move +
  // reconcile (shipped_qty / balance / status) + on_time_status recompute +
  // dashboard cache invalidation via the same path the import pipeline uses.
  moveGrn: (grn_id: number, schedule_id: number): Promise<AxiosResponse<unknown>> =>
    api.post(`/purchasing/grns/${grn_id}/link-schedule/`, { schedule_id }),

  // Closest-match auto-rebalance: walks GRNs in receipt order and lands
  // each whole onto its closest eligible schedule (1d early → 5d late);
  // never splits a GRN; over-fits flag is_over_delivery.
  rebalance: (po_line_id: number): Promise<AxiosResponse<{ moved: number; detail: string }>> =>
    api.post('/purchasing/grns/repair-po-line-rebalance/', { po_line_id }),

  // Chronological FIFO with split: walks schedules earliest-first and
  // fills each one to capacity, splitting GRNs as needed. Any qty left
  // over after every real schedule is full spills onto the latest active
  // schedule as over-delivery. Line-0 GRNs left untouched.
  rebalanceFifo: (po_line_id: number): Promise<AxiosResponse<{ moved: number; detail: string; spill_schedule_line?: string }>> =>
    api.post('/purchasing/grns/repair-po-line-rebalance-fifo/', { po_line_id }),

  // Per-allocation move — repoints a single GRNScheduleAllocation row to
  // another schedule on the same PO line. Doesn't touch the other
  // allocations on the same GRN (so split pieces move independently).
  moveAllocation: (allocation_id: number, schedule_id: number): Promise<AxiosResponse<unknown>> =>
    api.post('/purchasing/grns/repair-allocation-move/', { allocation_id, schedule_id }),

  // Split one allocation row into two on the same schedule. The user can
  // then drag the new piece elsewhere via moveAllocation.
  splitAllocation: (allocation_id: number, split_qty: string): Promise<AxiosResponse<unknown>> =>
    api.post('/purchasing/grns/repair-allocation-split/', { allocation_id, split_qty }),

  // Remove one allocation row. The schedule's shipped_qty drops by the
  // allocation's qty; the GRN's primary FK + verdict refresh from the
  // earliest remaining allocation (or clear if this was the only row).
  deleteAllocation: (allocation_id: number): Promise<AxiosResponse<unknown>> =>
    api.post('/purchasing/grns/repair-allocation-delete/', { allocation_id }),

  // Cancel a schedule line and transfer its GRNs to the active schedule
  // (else the nearest remaining real schedule, else leaves them unallocated).
  // The slot is flagged 'cancelled' and excluded from all future attribution.
  cancelSchedule: (schedule_id: number): Promise<AxiosResponse<{
    detail: string; schedule_id: number; transferred: number;
    target_schedule_id: number | null;
  }>> =>
    api.post('/purchasing/grns/repair-schedule-cancel/', { schedule_id }),

  // Un-cancel (reactivate) a buyer-cancelled schedule line. Flips it back to a
  // live status, recomputes balance from schedule_qty, reconciles + re-derives
  // the active pointer. Transferred GRNs are NOT pulled back — the slot reopens
  // as an empty open demand.
  uncancelSchedule: (schedule_id: number): Promise<AxiosResponse<{
    detail: string; schedule_id: number; status: string;
  }>> =>
    api.post('/purchasing/grns/repair-schedule-uncancel/', { schedule_id }),

  // Correct a schedule line's demanded quantity. Recomputes balance,
  // over-delivery flags, active pointer, and shipped/balance/status.
  setScheduleQty: (schedule_id: number, schedule_qty: string): Promise<AxiosResponse<{
    detail: string; schedule_id: number; schedule_qty: string;
    shipped_qty: string; balance_qty: string; status: string;
  }>> =>
    api.post('/purchasing/grns/repair-schedule-set-qty/', { schedule_id, schedule_qty }),

  // Background full recompute: reconcile shipped/balance from allocations,
  // recompute the active pointer + delivery dates, refresh OTIF, bust caches.
  // Does not re-point allocations. Settles any drift across all schedules.
  recalculate: (): Promise<AxiosResponse<{ detail: string; task_id: string | null }>> =>
    api.post('/purchasing/grns/repair-recalculate/', {}),
};
